import React, { useState } from 'react'
import { graphql } from 'gatsby'
import { decode } from 'html-entities'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown'
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight'

import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image'

import Breadcrumb from '../components/Breadcrumb'
import HTMLContent from '../components/Content'
import Layout from '../components/Layout'
import LeadCapture from '../components/LeadCapture'
import BgImage from '../components/BgImage'
import FixedCtaDisplay from '../components/FixedCtaDisplay'
import PorteaServices from '../components/PorteaServices'
import OfferPopup from '../components/OfferPopup'
import RelatedPages from '../components/RelatedPages'
import ScrollHandler from '../components/ScrollHandler'
import SEO from '../components/Seo'
import Video from '../components/Video'

import { canShowLeadForm } from '../utils/helpers'
import { prepareLinkElement } from '../utils/react_helper'
import ServiceTestimonials from '../components/ServiceTestimonials';


function PageTemplate({
  title: rawTitle,
  content,
  toc,
  pageContent,
  seo,
  acf,
  location,
  testimonials,
  ratings,
  physioAd,
  physioInfo,
  saswpCustomSchema
}) {
  const [showToc, setShowToc] = useState(false)

  const {
    section: sections,
    relatedPages,
    relatedLinks,
    references,
    leadFormHeading,
    leadFormCta,
    service,
    category,
    videoLinks,
  } = acf




  const title = decode(rawTitle)
  const breadcrumbs = [
    { target: '/', display: 'Home', keep: true },
    { target: `/${category.toLowerCase()}`, display: service, keep: true },

    { target: location, display: title },
  ]

  const [physioInfoMobile, physioInfoDesktop] = physioInfo
  const physioInfoSources = withArtDirection(physioInfoDesktop.childImageSharp.gatsbyImageData,[

    {
      image: physioInfoMobile.childImageSharp.gatsbyImageData,
      media: `(max-width: 768px)`,
    },
  ])

  const bgImage =
    acf.mainImage && acf.mainImage.localFile
      ? acf.mainImage.localFile.childImageSharp.gatsbyImageData
      : null



  return (
    <div style={{ paddingBottom: 20 }}>
      <SEO
        title={seo.title || title}
        description={seo.metaDesc || 'Healthcare at Home - Portea.com'}
        url={location}
        service={service}
        breadcrumbs={breadcrumbs}
        fullSchema={(saswpCustomSchema) ? JSON.parse(saswpCustomSchema) : null}
      />

      {canShowLeadForm(location) && (
        <ScrollHandler
          render={() => (
            <FixedCtaDisplay
              service={service}
              location={location}
              cta={leadFormCta}
              heading={leadFormHeading}
            />
          )}
        />
      )}

      <OfferPopup category={category} />
      <section className="hero">
        {bgImage && <BgImage alt="banner" image={bgImage} height="100%" loading="eager"/>}

        <div className="hero-body">
          <div className="container is-fluid">
            <div className="columns">
              <div className="column is-12">
                <div className=" level">
                  <div className="level-left">
                    <div>
                      <h1 className="title has-text-weight-bold is-bordered-text capitalize">
                        {decode(title.toLowerCase())}
                      </h1>
                    </div>
                  </div>

                  <div className="level-right">
                    <div className="leadCtr level-item">
                      {canShowLeadForm(location) && (
                        <LeadCapture
                          service={service}
                          location={location}
                          cta={leadFormCta}
                          heading={leadFormHeading}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb breadcrumbs={breadcrumbs} />

      <div className="container is-fluid">
        <div className="columns">
          <div className="column is-12 page-subtitle">
            <h2 className="subtitle has-text-centered">{acf.sub_title}</h2>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="container is-fluid">
          <div className="columns">
            <div className="column is-12">
              {category === 'Physiotherapy' && (
                <GatsbyImage alt="How To" className='page-howto' image={physioInfoSources} />
              )}
            </div>
          </div>
        </div>
      </div>

      {relatedPages && <RelatedPages relatedPages={relatedPages} />}

      <div className="section">
        <div className="container is-fluid">
          <div className="columns">
            <div className="column is-9">
              {toc.length > 0 && (
                <aside className="menu">
                  <div className="menu-header">
                    <p className="menu-label">Table of Contents</p>
                    <div className="icon-action">
                      <FontAwesomeIcon
                        icon={showToc ? faAngleUp : faAngleDown }
                        color="black"
                        border
                        className="menu-icon"
                        onClick={() => setShowToc(!showToc)}
                      />
                    </div>
                  </div>
                    <ul className={`menu-list is-collapsible ${!showToc && 'is-hidden'}`}>
                      {toc.map(({ href, title: linkTitle }) => (
                        <li key={href}>
                          <a href={href}>
                            <span>{linkTitle}</span>
                            <FontAwesomeIcon
                              icon={faAngleRight}
                              color="black"
                              className="menu-icon"
                            />
                          </a>
                        </li>
                      ))}
                    </ul>
                </aside>
              )}

              {category === 'Physiotherapy' && (
                <a href="https://lp.portea.com/physiotherapy-300-cb/?utm_source=banner-image&utm_medium=organic&utm_campaign=image2LP&utm_content=traffic_organic_banner2LP">
                  <GatsbyImage alt="" image={physioAd.childImageSharp.gatsbyImageData} />
                </a>
              )}

              {videoLinks &&
                videoLinks.map(({ videoLink }) => (
                  <Video
                    key={videoLink.url}
                    videoSrcURL={videoLink.url}
                    videoTitle={videoLink.title}
                  />
                ))}

              {pageContent.map(({ content: pc }) => (
                <HTMLContent key={pc} className="content" content={pc} />
              ))}

              {sections &&
                sections
                  .filter((c) => c.heading != null)
                  .map((c) => (
                    <div className="section content-section" key={c.heading}>
                      <h2 className="is-size-3 has-text-primary has-text-weight-bold capitalize">
                        {c.heading.toLowerCase()}
                      </h2>

                      <HTMLContent
                        className="content"
                        content={c.description}
                      />
                    </div>
                  ))}

              {references && (
                <div className="container is-fluid content">
                  <h2>References</h2>
                  <ul className="menu-list">
                    {references.map(({ referenceLink }) => (
                      <li className="wrap-content" key={referenceLink}>
                        {prepareLinkElement(referenceLink, referenceLink)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="column right-panel">

              {relatedLinks &&
                relatedLinks
                  .filter((r) => r.heading != null)
                  .map(({ heading, linkContent }) => (
                    <nav className="panel" key={heading}>
                      <p className="panel-heading has-text-weight-bold">
                        {heading.toLowerCase()}
                      </p>

                      {linkContent &&
                        linkContent.map(({ link: { url, title: t } }) => (
                          <React.Fragment key={t}>
                            {prepareLinkElement(url, t, 'panel-block')}
                          </React.Fragment>
                        ))}
                    </nav>
                  ))}

              <PorteaServices />
            </div>
          </div>
        </div>
      </div>
      <ServiceTestimonials testimonials={testimonials} />
    </div>
  );
}

function Page({ data, pageContext }) {

  const {
    wpPage: page,
    allWpTestimonial: testimonials,
    allWpRating: ratings,
    physioAd,
    physioInfoMobile,
    physioInfoDesktop,
  } = data

  const {toc, pageContent} = pageContext;

  const acf = {
    ...page.sections,
    ...page.serviceType,
    ...page.leadCaptureForm,
    ...page.videoLinks,
  }

  return (
    <Layout>
      <PageTemplate
        title={page.title}
        content={page.content}
        toc={toc}
        pageContent={pageContent}
        acf={acf}
        seo={page.seo}
        saswpCustomSchema={page.saswpCustomSchema.json_ld}
        location={page.fields.location}
        testimonials={testimonials}
        ratings={ratings}
        physioAd={physioAd}
        physioInfo={[physioInfoMobile, physioInfoDesktop]}
      />
    </Layout>
  )
}

export default Page

export const pageQuery = graphql`query PageById($id: String!, $category: String) {
  wpPage(id: {eq: $id}) {
    title
    content
    fields {
      location
    }
    seo {
      metaDesc
      title
    }
    saswpCustomSchema {
      json_ld
    }
    serviceType {
      category
      service
    }
    leadCaptureForm {
      leadFormCta
      leadFormHeading
    }
    sections {
      subTitle
      section {
        heading
        description
      }
      relatedPages {
        ... on WpPost {
          id
          link
          title
          sections {
            secondaryImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600, layout: CONSTRAINED)
                }
              }
            }
          }
        }
        ... on WpMedicalEquipment {
          id
          link
          title
          sections {
            secondaryImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600, layout: CONSTRAINED)
                }
              }
            }
          }
        }
        ... on WpPage {
          id
          link
          title
          sections {
            secondaryImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      }
      relatedLinks {
        heading
        linkContent {
          link {
            target
            title
            url
          }
        }
      }
      references {
        referenceLink
      }
      mainImage {
        localFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
    videoLinks {
      videoLinks {
        videoLink {
          title
          url
        }
      }
    }
  }
  allWpTestimonial(filter: {serviceType: {category: {eq: $category}}}, limit: 10) {
    edges {
      node {
        title
        content
        excerpt
        serviceType {
          category
        }
        userDetails {
          userName
          details
          avatar {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 96, height: 96, layout: FIXED)
              }
            }
          }
        }
      }
    }
  }
  allWpRating(filter: {serviceType: {category: {eq: $category}}}) {
    edges {
      node {
        id
        title
        serviceType {
          category
        }
        ratingInfo {
          ratingDescription
          ratingScore
          ratingUrl
        }
      }
    }
  }
  physioAd: file(relativePath: {eq: "ads/physio_ad_10.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  physioInfoMobile: file(relativePath: {eq: "info/physioInfoMobile.png"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  physioInfoDesktop: file(relativePath: {eq: "info/physioInfoDesktop.png"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
}`
